import { deduplicateEvents } from '@reflex/ui/client/event-utils';
import type { AgentStreamPageInfo, PagedAgentStreamResponse } from '@reflex/shared';
import type { AxonEvent } from '@/lib/types';

/**
 * Pure helpers for the paged session-open model: the stream request opts
 * into the server's turn-aligned window (`paged=true`), and these helpers
 * normalize the two response shapes and merge older pages into the cache.
 *
 * The response is the discriminator: an array means the agent has no
 * session index (legacy path, identical behavior to before paging
 * existed); the `{ events, page }` envelope means the server returned the
 * initial window and older history is fetched with `before` cursors.
 */

export interface NormalizedStream {
  events: AxonEvent[];
  /** Null for legacy (unindexed) agents — the full history was returned. */
  page: AgentStreamPageInfo | null;
}

export function normalizeStreamResponse(
  response: AxonEvent[] | PagedAgentStreamResponse,
): NormalizedStream {
  if (Array.isArray(response)) return { events: response, page: null };
  return { events: response.events as AxonEvent[], page: response.page };
}

/**
 * Prepend an earlier page's events. Older history goes first (the cache is
 * chronological) and the existing dedup keeps any overlap harmless — the
 * reducer's rebuild-on-prefix-change handles the rest.
 */
export function mergeEarlierEvents(existing: AxonEvent[], earlier: AxonEvent[]): AxonEvent[] {
  return deduplicateEvents([...earlier, ...existing]);
}

/**
 * What the socket should subscribe with, given the initial fetch's page
 * state: nothing until the fetch resolves (`page === undefined` — a paged
 * agent must not take the full replay its fetch just avoided), a full
 * replay for legacy agents (`page === null`), and a live-only replay from
 * the window head for paged agents.
 */
export function subscriptionForPage(
  streamId: string | null,
  page: AgentStreamPageInfo | null | undefined,
): { streamId: string | null; afterSequence: number | undefined } {
  if (page === undefined) return { streamId: null, afterSequence: undefined };
  return { streamId, afterSequence: page?.endSeq ?? undefined };
}

/**
 * Merge a reconnect's fresh newest-window snapshot into the cache. The
 * cache goes first: it already holds older loaded pages the window does
 * not cover, and every window event the cache lacks is strictly newer
 * than anything cached (the window runs to the live head), so appending
 * keeps chronology. Contrast with the legacy full-snapshot merge, where
 * the snapshot is the canonical ordering and goes first.
 */
export function mergeFreshWindow(existing: AxonEvent[], windowEvents: AxonEvent[]): AxonEvent[] {
  return deduplicateEvents([...existing, ...windowEvents]);
}

/**
 * The page info after loading one earlier page: the loaded range now
 * starts where the fetched page starts, while `endSeq` (the live head the
 * socket subscribed after) stays put.
 */
export function mergeEarlierPageInfo(
  current: AgentStreamPageInfo,
  earlier: AgentStreamPageInfo,
): AgentStreamPageInfo {
  return {
    startSeq: earlier.startSeq,
    endSeq: current.endSeq,
    prevCursor: earlier.prevCursor,
    hasMore: earlier.hasMore,
  };
}
